import { useState } from "react";
import {
    Box,
    Card,
    Chip,
    CircularProgress,
    Grid,
    Paper,
    Typography,
    useTheme,
} from "@mui/material";
import {
    Warning as WarningIcon,
    Error as ErrorIcon,
} from "@mui/icons-material";
import { RiskCategory } from "../types";
import { useRiskSummary, useRisks } from "../hooks";

export const RiskAnticipator = () => {
    const theme = useTheme();
    const [selectedCategory, setSelectedCategory] = useState<RiskCategory>("all");

    // Fetch risk summary and risks
    const { data: riskSummary, isLoading: isSummaryLoading } = useRiskSummary();
    const { data: risks = [], isLoading: isRisksLoading } = useRisks(selectedCategory);

    const getCategoryCount = (category: RiskCategory): number => {
        if (category === "all") return riskSummary?.total ?? 0;
        // For other categories, we need to filter all risks
        return risks.filter(risk => risk.category === category).length;
    };

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case "critical":
                return theme.palette.error.main;
            case "high":
                return theme.palette.error.main;
            case "medium":
                return theme.palette.warning.main;
            case "low":
                return theme.palette.success.main;
            default:
                return theme.palette.grey[500];
        }
    };
    
    if (isSummaryLoading) {
        return (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100%", overflow: "auto", px: 2, py: 3 }}>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

                {/* Risk Summary Cards */}
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                Total Risks
                            </Typography>
                            <Typography variant="h5">
                                {riskSummary?.total ?? 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderLeft: `4px solid ${theme.palette.error.main}`,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Critical Severity
                            </Typography>
                            <Typography variant="h5" color="error.main">
                                {riskSummary?.critical ?? 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderLeft: `4px solid ${theme.palette.error.main}`,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                High Severity
                            </Typography>
                            <Typography variant="h5" color="error.main">
                                {riskSummary?.high ?? 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderLeft: `4px solid ${theme.palette.warning.main}`,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Medium Severity
                            </Typography>
                            <Typography variant="h5" color="warning.main">
                                {riskSummary?.medium ?? 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderLeft: `4px solid ${theme.palette.success.main}`,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Low Severity
                            </Typography>
                            <Typography variant="h5" color="success.main">
                                {riskSummary?.low ?? 0}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                <Card variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>

                    {/* Risk Overview Section */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: .5 }}>
                        <Typography variant="body2">
                            Risk Overview
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Emerging delivery risks detected using current plan data. Sorted by severity — budget — age.
                        </Typography>
                    </Box>

                    {/* Category Filters */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, p: 1, borderRadius: 1 }}>
                        <Chip
                            label={`All Risks (${getCategoryCount("all")})`}
                            variant={selectedCategory === "all" ? "filled" : "outlined"}
                            color={selectedCategory === "all" ? "primary" : "default"}
                            onClick={() => setSelectedCategory("all")}
                        />
                        <Chip
                            label={`Critical Path (${getCategoryCount("critical-path")})`}
                            variant={selectedCategory === "critical-path" ? "filled" : "outlined"}
                            color={selectedCategory === "critical-path" ? "primary" : "default"}
                            onClick={() => setSelectedCategory("critical-path")}
                        />
                        <Chip
                            label={`Commissioning (${getCategoryCount("commissioning")})`}
                            variant={selectedCategory === "commissioning" ? "filled" : "outlined"}
                            color={selectedCategory === "commissioning" ? "primary" : "default"}
                            onClick={() => setSelectedCategory("commissioning")}
                        />
                        <Chip
                            label={`Close-out (${getCategoryCount("close-out")})`}
                            variant={selectedCategory === "close-out" ? "filled" : "outlined"}
                            color={selectedCategory === "close-out" ? "primary" : "default"}
                            onClick={() => setSelectedCategory("close-out")}
                        />
                    </Box>

                    {/* Risk Cards Grid */}
                    <Grid container spacing={2}>
                        {isRisksLoading ? (
                            <Grid size={12}>
                                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            </Grid>
                        ) : (
                            risks.map((risk) => (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={risk.id}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: "100%",
                                        borderLeft: `4px solid ${getSeverityColor(risk.severity)}`,
                                        "&:hover": {
                                            boxShadow: theme.shadows[4],
                                            cursor: "pointer",
                                        },
                                    }}
                                >
                                    <Box sx={{ p: 2 }}>
                                        {/* Header */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                mb: 1,
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                {risk.type === "commissioning" ? (
                                                    <WarningIcon color="error" fontSize="small" />
                                                ) : (
                                                    <ErrorIcon color="error" fontSize="small" />
                                                )}
                                                <Typography variant="subtitle2">
                                                    {risk.type === "commissioning" ? "Commissioning Risk" : "Close-out Risk"}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={risk.severity.charAt(0).toUpperCase() + risk.severity.slice(1)}
                                                size="small"
                                                color="error"
                                            />
                                        </Box>

                                        {/* Project Name */}
                                        <Typography variant="caption" sx={{ mb: 2 }}>
                                            {risk.projectName}
                                        </Typography>

                                        {/* Details */}
                                        <Box sx={{ mb: 2 }}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Current Phase:
                                                </Typography>
                                                <Typography variant="caption">
                                                    {risk.currentPhase}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Budget:
                                                </Typography>
                                                <Typography variant="caption">
                                                    £{(risk.budget / 1000000).toFixed(1)}M
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Project ID:
                                                </Typography>
                                                <Typography variant="caption">
                                                    {risk.projectId}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Description */}
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                display: "block",
                                                lineHeight: 1.5,
                                                bgcolor: "rgba(0, 0, 0, 0.04)",
                                                p: 2,
                                                borderRadius: 1,
                                            }}
                                        >
                                            {risk.description}
                                        </Typography>
                                    </Box>
                                </Card>
                            </Grid>
                        )))}
                    </Grid>

                    {!isRisksLoading && risks.length === 0 && (
                        <Box
                            sx={{
                                textAlign: "center",
                                py: 8,
                            }}
                        >
                            <Typography variant="body1" color="text.secondary">
                                No risks found in this category.
                            </Typography>
                        </Box>
                    )}

                </Card>

                <Card variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>

                    {/* Critical Path Integrity Risk Detection Logic */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: .5 }}>
                        <Typography variant="body2">
                            Critical Path Integrity Risk Detection Logic
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            How Vision Studio identifies planning credibility and delivery feasibility issues
                        </Typography>
                    </Box>


                    {/* Important Note */}
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: "rgba(25, 118, 210, 0.08)",
                            borderLeft: `4px solid ${theme.palette.info.main}`,
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2">
                            <strong>Important:</strong> Critical Path Integrity Risks highlight where a delivery plan cannot currently demonstrate a credible path to key milestones and may inform Jeopardy decisions if unresolved.
                        </Typography>
                    </Box>

                    {/* Missing Critical Path */}
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: "rgba(211, 47, 47, 0.08)",
                            borderLeft: `4px solid ${theme.palette.error.main}`,
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2">
                            <strong style={{ color: theme.palette.error.main }}>Missing Critical Path:</strong> A downstream phase has a planned start date, but prerequisite phases do not form a complete, defined path leading to that start. Example: Construction planned to start on 26 Jan 2026, but no complete path is defined showing how Detailed Design and Pre-Construction will complete before this date.
                        </Typography>
                    </Box>

                    {/* Critical Path Misalignment */}
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: "rgba(211, 47, 47, 0.08)",
                            borderLeft: `4px solid ${theme.palette.error.main}`,
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2">
                            <strong style={{ color: theme.palette.error.main }}>Critical Path Misalignment:</strong> The planned completion of prerequisite phases extends beyond the planned start of a downstream phase. Example: Detailed Design planned to complete 15 Feb 2026, but Construction planned to start 26 Jan 2026. The delivery plan is internally inconsistent and Construction start date is not achievable under the current plan.
                        </Typography>
                    </Box>

                    {/* Critical Path Assumption Risk */}
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: "rgba(211, 47, 47, 0.08)",
                            borderLeft: `4px solid ${theme.palette.error.main}`,
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2">
                            <strong style={{ color: theme.palette.error.main }}>Critical Path Assumption Risk:</strong> A critical path is defined, but one or more prerequisite phases rely on placeholder or low-confidence planning assumptions. Example: Construction start assumes Design completes in 2 weeks, but the Design phase has no detailed plan or confirmed duration. Downstream confidence is artificially high.
                        </Typography>
                    </Box>

                    {/* Commissioning Risk */}
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: "background.paper",
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2">
                            <strong>Commissioning Risk:</strong> Today &gt; Planned Commissioning Date, but project not yet in Commissioning or Closure phase.
                        </Typography>
                    </Box>

                    {/* Close-out Risk */}
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: "background.paper",
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2">
                            <strong>Close-out Risk:</strong> Today &gt; Planned Close-out Date, but project not yet in Closure phase.
                        </Typography>
                    </Box>

                    {/* Note */}
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: "rgba(237, 108, 2, 0.08)",
                            borderLeft: `4px solid ${theme.palette.warning.main}`,
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2">
                            <strong>Note:</strong> Phase Breach indicators based solely on exceeding planned phase durations are not used as primary Risk Anticipator signals. Focus is on plan credibility, not lateness.
                        </Typography>
                    </Box>

                </Card>

            </Box>

        </Box>
    );
};
